/**
 * @fileoverview Share & Earn section for the hamburger menu.
 * Displays referral progress, copy-link row, and branded QR code.
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUniversalCredits } from '@/hooks/useUniversalCredits';
import { useHaptics } from '@/hooks/useHaptics';
import { Clipboard } from '@capacitor/clipboard';
import { Check, Copy, Gift, Lock, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import qrcode from 'qrcode-generator';

// ---------------------------------------------------------------------------
// Milestones
// ---------------------------------------------------------------------------

const MILESTONES = [
{ count: 5, badge: 'connector', label: 'Connector', perk: 'Exclusive badge' },
{ count: 10, badge: 'ambassador', label: 'Ambassador', perk: 'Early access' },
{ count: 25, badge: 'tastemaker', label: 'Tastemaker', perk: 'VIP beta' },
{ count: 50, badge: 'legend', label: 'Icon', perk: 'Lifetime perks' }] as
const;

// ---------------------------------------------------------------------------
// Branded QR Code
// ---------------------------------------------------------------------------

function BrandedQRCode({ url }: {url: string;}) {
  const svgContent = useMemo(() => {
    const qr = qrcode(0, 'H'); // type 0 = auto, ecl H = high
    qr.addData(url);
    qr.make();

    const moduleCount = qr.getModuleCount();
    const cellSize = 6;
    const margin = 2;
    const size = (moduleCount + margin * 2) * cellSize;
    const centerSize = Math.floor(moduleCount * 0.28);
    const centerStart = Math.floor((moduleCount - centerSize) / 2);
    const centerEnd = centerStart + centerSize;
    const r = cellSize * 0.38; // rounded radius

    let paths = '';
    for (let row = 0; row < moduleCount; row++) {
      for (let col = 0; col < moduleCount; col++) {
        if (!qr.isDark(row, col)) continue;
        // Skip center area for logo
        if (row >= centerStart && row < centerEnd && col >= centerStart && col < centerEnd) continue;
        const x = (col + margin) * cellSize;
        const y = (row + margin) * cellSize;
        paths += `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" rx="${r}" ry="${r}" />`;
      }
    }

    // W logo in center
    const logoX = (centerStart + margin) * cellSize;
    const logoY = (centerStart + margin) * cellSize;
    const logoSize = centerSize * cellSize;

    return { paths, size, logoX, logoY, logoSize };
  }, [url]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="bg-white rounded-2xl p-2 shadow-sm border border-border/20">
        <svg
          viewBox={`0 0 ${svgContent.size} ${svgContent.size}`}
          width="160"
          height="160"
          xmlns="http://www.w3.org/2000/svg">

          <defs>
            <linearGradient id="qr-coral" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#FF5B4D" style={{ stopColor: 'color(display-p3 1 0.3595 0.3021)' } as React.CSSProperties} />
              <stop offset="100%" stopColor="#FF6F3B" style={{ stopColor: 'color(display-p3 1 0.4366 0.2317)' } as React.CSSProperties} />
            </linearGradient>
          </defs>
          <g fill="url(#qr-coral)" dangerouslySetInnerHTML={{ __html: svgContent.paths }} />
          {/* Centered W logo */}
          <g transform={`translate(${svgContent.logoX + svgContent.logoSize * 0.1}, ${svgContent.logoY + svgContent.logoSize * 0.15})`}>
            <svg
              width={svgContent.logoSize * 0.8}
              height={svgContent.logoSize * 0.7}
              viewBox="0 0 160 122"
              fill="none">

              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M39.5241 0.0195312C44.7941 0.247619 49.6059 2.55218 53.067 6.21094C56.7167 2.35288 61.8686 0 67.4752 0H92.525C98.1314 9.17377e-05 103.283 2.35307 106.932 6.21094C110.582 2.35236 115.735 3.24521e-05 121.342 0H140.171C146.277 0 152.044 2.81403 155.801 7.62695C159.559 12.4398 160.889 18.716 159.408 24.6396L138.941 106.504C136.734 115.331 128.804 121.524 119.705 121.524H94.6549C88.922 121.524 83.662 119.066 79.9996 115.051C76.3369 119.066 71.0781 121.524 65.3463 121.524H40.2955C31.1965 121.524 23.2651 115.331 21.0582 106.504L0.592419 24.6396C-0.888516 18.7159 0.441225 12.4398 4.19886 7.62695C7.95662 2.81411 13.7227 8.58427e-05 19.8287 0H38.6588L39.5241 0.0195312ZM19.8278 19.8301L40.2946 101.694H65.3463L80.0807 38.0049L94.6539 101.694H119.704L140.17 19.8301H121.341L106.933 83.3574L92.5241 19.8301H67.4743L53.0661 83.3574L38.6578 19.8301H19.8278Z"
                fill="url(#qr-coral)" />

            </svg>
          </g>
        </svg>
      </div>
      <p className="text-[11px] text-muted-foreground font-medium">Have a friend scan to join</p>
    </div>);

}

// ---------------------------------------------------------------------------
// Main Section
// ---------------------------------------------------------------------------

export function ReferralShareSection() {
  const { user } = useAuth();
  const { referralLink } = useUniversalCredits();
  const { success: hapticSuccess } = useHaptics();
  const [copied, setCopied] = useState(false);

  const { data: referralCount = 0 } = useQuery({
    queryKey: ['referral-count', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      const { count } = await supabase.
      from('referrals').
      select('*', { count: 'exact', head: true }).
      eq('referrer_id', user.id);
      return count || 0;
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000
  });

  if (!user || !referralLink) return null;

  const shortLink = referralLink.replace('https://', '');
  const maxMilestone = MILESTONES[MILESTONES.length - 1].count;
  const progressPercent = Math.min(100, referralCount / maxMilestone * 100);

  const handleCopy = async () => {
    try {
      try {
        await Clipboard.write({ string: referralLink });
      } catch {
        await navigator.clipboard.writeText(referralLink);
      }
      setCopied(true);
      hapticSuccess();
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  return (
    <div className="px-4 py-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Gift className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Invite Friends & Rank Up

        </span>
      </div>

      {/* Referral count */}
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground text-center mb-4">
          <span className="text-foreground font-semibold">{referralCount}</span> friend{referralCount !== 1 ? 's' : ''} invited
        </p>

        {/* Milestone markers - evenly spaced */}
        <div className="flex justify-between items-start">
          {MILESTONES.map((m, i) => {const reached = referralCount >= m.count;const isNext = !reached && (i === 0 || referralCount >= MILESTONES[i - 1].count);
            return (
              <div key={m.badge} className="flex flex-col items-center gap-0.5 w-14">
                <div
                  className={cn(
                    'w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all',
                    reached ?
                    'bg-primary border-primary text-primary-foreground shadow-md shadow-primary/30' :
                    isNext ?
                    'bg-background border-primary/60 text-primary animate-pulse' :
                    'bg-muted border-border text-muted-foreground'
                  )}>

                  {reached ? <Star className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                </div>
                <span className={cn(
                  'text-[10px] font-semibold',
                  reached ? 'text-primary' : 'text-muted-foreground'
                )}>
                  {m.count}
                </span>
                <span className={cn(
                  'text-[9px] leading-tight text-center',
                  reached ? 'text-foreground' : 'text-muted-foreground/60'
                )}>
                  {m.label}
                </span>
              </div>);

          })}
        </div>

        {/* Progress bar */}
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${progressPercent}%` }} />

        </div>
      </div>

      {/* Copy link row */}
      <button
        onClick={handleCopy}
        className="w-full flex items-center gap-2 bg-muted/60 rounded-xl px-3 py-2.5 text-left active:scale-[0.98] transition-transform">

        <span
          className="flex-1 text-xs text-muted-foreground truncate font-mono"
          style={{ fontSize: '11px' }}>

          {shortLink}
        </span>
        <span className={cn(
          'flex items-center gap-1 text-xs font-semibold shrink-0 transition-colors',
          copied ? 'text-primary/80' : 'text-primary'
        )}>
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </span>
      </button>

      {/* QR Code */}
      <BrandedQRCode url={referralLink} />
    </div>);

}